import type { Bar, LoadedData, Note, Variation } from '../types';
import { NUM_BAR, NUM_VAR } from '../constants';

const VARIATIONS_URL = new URL('../../data/variations.json', import.meta.url).href;
const OFFSETS_URL = new URL('../../data/offsets.json', import.meta.url).href;

type RawVariation = { notes: Note[] };
type RawBar = { first: number; last: number };

export async function loadAllData(): Promise<LoadedData> {
  const [variationsResp, offsetsResp] = await Promise.all([
    fetch(VARIATIONS_URL),
    fetch(OFFSETS_URL),
  ]);
  if (!variationsResp.ok) throw new Error(`Failed to fetch variations.json: ${variationsResp.status}`);
  if (!offsetsResp.ok) throw new Error(`Failed to fetch offsets.json: ${offsetsResp.status}`);
  const rawVariations = (await variationsResp.json()) as RawVariation[];
  const rawOffsets = (await offsetsResp.json()) as RawBar[][];

  if (rawVariations.length !== NUM_VAR) {
    throw new Error(`Expected ${NUM_VAR} variations, got ${rawVariations.length}`);
  }
  if (rawOffsets.length !== NUM_VAR) {
    throw new Error(`Expected ${NUM_VAR} offsets, got ${rawOffsets.length}`);
  }

  const variations: Variation[] = rawVariations.map((rv, i) => {
    if (!Array.isArray(rv.notes) || rv.notes.length === 0) {
      throw new Error(`Variation ${i} has no notes`);
    }
    return { notes: rv.notes };
  });

  const offsets: Bar[][] = rawOffsets.map((bars, i) => {
    if (bars.length !== NUM_BAR) {
      throw new Error(`Variation ${i} expected ${NUM_BAR} bars, got ${bars.length}`);
    }
    const last = bars[NUM_BAR - 1]!.last;
    if (last >= variations[i]!.notes.length) {
      throw new Error(
        `Variation ${i}: last bar.last=${last} >= notes.length=${variations[i]!.notes.length}`,
      );
    }
    return bars;
  });

  return { variations, offsets };
}
